import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Icon } from "@iconify/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAuth } from "../contexts/JWTContext";
import s from "./Menu.module.css";
import { menuTabs, menuSections } from "../components/Submenu/menuTabs";
import CounterBonus from "../components/CounterBonus";
import CheckinExperience from "../components/CheckinExperience";
import ContactModal from "../components/Modals/ContactModal";

const TOAST_TONE = {
  info: { icon: "solar:info-circle-bold", color: "#f7e3a1" },
  warning: { icon: "solar:danger-triangle-bold", color: "#ffb34d" },
  error: { icon: "solar:close-circle-bold", color: "#ff7a7a" },
};

const Toast = ({ message, type = "warning", show, onClose }) => {
  useEffect(() => {
    if (!show) return undefined;
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [show, onClose]);

  if (!show) return null;
  const tone = TOAST_TONE[type] || TOAST_TONE.warning;

  return (
    <div className={s.toast} role="status" style={{ "--tone": tone.color }}>
      <Icon icon={tone.icon} width={22} className={s.toastIcon} />
      <span className={s.toastMsg}>{message}</span>
      <button type="button" onClick={onClose} className={s.toastClose} aria-label="ปิด">
        ×
      </button>
    </div>
  );
};

const money = (v) =>
  Number(v || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const Menu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isInitialized } = useAuth();
  const currentUser = useSelector((state) => state.user.currentUser);
  const singleUsers = useSelector((state) => state.user.singleUsers);
  const playerBalance = useSelector((state) => state.user.playerBalance);
  const playerCash = useSelector((state) => state.user.playerCash);
  const wheelPoint = useSelector((state) => state.user.wheelPoint);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "warning",
  });

  const rawTab = new URLSearchParams(location.search).get("tab");
  const activeTab = rawTab === "cashier" ? "deposit" : rawTab;
  const activeTabConfig = menuTabs.find(
    (tab) => tab.id === activeTab && tab.component,
  );
  const ActiveComp = activeTabConfig?.component;

  const showToast = useCallback((message, type = "warning") => {
    setToast({ show: true, message, type });
  }, []);

  const hideToast = useCallback(() => {
    setToast({ show: false, message: "", type: "warning" });
  }, []);

  useEffect(() => {
    // wait for the stored session, or a refresh would bounce members out
    if (isInitialized && !isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [activeTab]);

  const openItem = (tabId) => {
    if (tabId === "contact") {
      setIsContactModalOpen(true);
      return;
    }
    navigate(`/menu?tab=${tabId}`);
  };

  // Get current user from singleUsers for CheckinExperience
  const currentUserForCheckin = useMemo(() => {
    if (!singleUsers) return null;
    if (Array.isArray(singleUsers)) {
      return singleUsers[0] || null;
    }
    if (Array.isArray(singleUsers?.data)) {
      return singleUsers.data[0] || null;
    }
    return singleUsers;
  }, [singleUsers]);

  if (!isAuthenticated) return null;

  const username =
    currentUser?.username ||
    currentUser?.name ||
    currentUser?.userName ||
    "ผู้ใช้งาน";
  const withdrawConditionDetail = currentUser?.withdrawConditionDetail;
  const promotionDetail = currentUser?.PromotionDetail;
  const isPromoActive = currentUser?.PromotionStatus === "ACTIVE";
  const balance = Array.isArray(playerBalance) ? playerBalance[0] : playerBalance;

  return (
    <div className={s.page}>
      <CheckinExperience
        currentUser={currentUserForCheckin}
        showToast={showToast}
      >
        <CounterBonus />
      </CheckinExperience>

      <div className={`lux-container ${s.layout}`} data-view={ActiveComp ? "tab" : "home"}>
        {/* ---------------- member card + navigation ---------------- */}
        <aside className={s.side}>
          <section className={s.member}>
            <div className={s.memberTop}>
              <span className={s.memberAvatar}>
                <img src="/lux/icons/vip.webp" alt="" />
              </span>
              <div className={s.memberWho}>
                <span className="lux-eyebrow">Member</span>
                <strong>{username}</strong>
              </div>
            </div>

            <div className={s.credit}>
              <span className={s.creditLabel}>เครดิตคงเหลือ</span>
              <span className={s.creditValue}>
                <small>฿</small>
                {money(balance)}
              </span>
            </div>

            <div className={s.wallets}>
              <div className={s.wallet}>
                <Icon icon="solar:wallet-money-bold-duotone" width={18} />
                <span className={s.walletText}>
                  <small>เงินสด</small>
                  <b>฿ {Number(playerCash || 0).toLocaleString("th-TH")}</b>
                </span>
              </div>
              <div className={s.wallet}>
                <Icon icon="ri:copper-diamond-fill" width={18} />
                <span className={s.walletText}>
                  <small>แต้มเพชร</small>
                  <b>{Number(wheelPoint || 0).toLocaleString("th-TH")}</b>
                </span>
              </div>
            </div>

            <div className={s.memberActions}>
              <button
                type="button"
                className="lux-btn lux-btn--gold lux-btn--block"
                onClick={() => openItem("deposit")}
              >
                <Icon icon="solar:card-recive-bold" width={18} />
                <span>ฝากเงิน</span>
              </button>
              <button
                type="button"
                className="lux-btn lux-btn--ghost lux-btn--block"
                onClick={() => navigate("/home")}
              >
                <Icon icon="solar:play-bold" width={16} />
                <span>เข้าเกม</span>
              </button>
            </div>
          </section>

          <nav className={s.nav} aria-label="เมนูสมาชิก">
            {menuSections.map((section) => (
              <div key={section.id} className={s.navGroup}>
                <span className={s.navHead}>{section.label}</span>
                {menuTabs
                  .filter((tab) => tab.group === section.id)
                  .map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`${s.navItem} ${tab.id === activeTab ? s.navOn : ""}`}
                      aria-current={tab.id === activeTab ? "page" : undefined}
                      onClick={() => openItem(tab.id)}
                    >
                      <Icon icon={tab.icon} width={20} />
                      <span>{tab.label}</span>
                    </button>
                  ))}
              </div>
            ))}
          </nav>
        </aside>

        {/* ---------------- content ---------------- */}
        <section className={s.content}>
          {ActiveComp ? (
            <>
              <header className={s.tabHead}>
                <button
                  type="button"
                  className={s.back}
                  onClick={() => navigate("/menu")}
                  aria-label="กลับเมนูสมาชิก"
                >
                  <Icon icon="solar:alt-arrow-left-linear" width={22} />
                </button>
                <span className={s.tabIcon}>
                  <Icon icon={activeTabConfig.icon} width={24} />
                </span>
                <div className={s.tabTitle}>
                  <h1>{activeTabConfig.label}</h1>
                  {activeTabConfig.desc && <p>{activeTabConfig.desc}</p>}
                </div>
              </header>
              <div className={s.tabBody}>
                <ActiveComp />
              </div>
            </>
          ) : (
            <>
              <section className={s.welcome}>
                <div className={s.welcomeCopy}>
                  <span className="lux-eyebrow">Welcome back</span>
                  <h1>
                    สวัสดี, <b className="lux-gold-text">{username}</b>
                  </h1>
                  <p>จัดการบัญชี การเงิน และกิจกรรมของคุณได้ในที่เดียว</p>
                </div>
                <img src="/lux/hero/girl-dress.webp" alt="" aria-hidden="true" />
              </section>

              {(withdrawConditionDetail || promotionDetail) && (
                <div className={s.notices}>
                  {withdrawConditionDetail && (
                    <div className={`${s.notice} ${s.noticeWarn}`}>
                      <Icon icon="solar:shield-warning-bold" width={22} />
                      <div className={s.noticeText}>
                        <strong>ติดเงื่อนไขถอน</strong>
                        <span>กรุณาทำเทิร์นให้ครบก่อนถอน</span>
                        <small>{withdrawConditionDetail}</small>
                      </div>
                    </div>
                  )}
                  {promotionDetail && (
                    <div className={s.notice}>
                      <Icon icon="solar:gift-bold" width={22} />
                      <div className={s.noticeText}>
                        <strong>
                          {isPromoActive ? "กำลังใช้โปรโมชั่น" : "เลือกโปรโมชั่นแล้ว"}
                        </strong>
                        <span>{promotionDetail}</span>
                        {!isPromoActive && <small>ฝากเงินเพื่อเปิดใช้งานโปรนี้</small>}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="lux-head">
                <div className="lux-head__left">
                  <h2 className="lux-head__title">
                    เมนู<b>ทั้งหมด</b>
                  </h2>
                </div>
              </div>

              <div className={s.tiles}>
                {menuTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={s.tile}
                    onClick={() => openItem(tab.id)}
                  >
                    <span className={s.tileIcon}>
                      <Icon icon={tab.icon} width={28} />
                    </span>
                    <span className={s.tileLabel}>{tab.shortLabel || tab.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
      <Toast
        message={toast.message}
        show={toast.show}
        type={toast.type}
        onClose={hideToast}
      />
    </div>
  );
};

export default Menu;
